import { useState, useEffect, useCallback } from 'react'
import { getNexusClient } from '@/lib/supabase/client'
import { mailApi, MailMessage, MailFolder, DEFAULT_FOLDERS } from '@/lib/supabase/mail'
import { useEmailAccounts, EmailAccount } from './useEmailAccounts'
import { useAuth } from '@/lib/auth/context'
import { CachedEmailRow, EmailFolderRow } from '@/lib/supabase/types'

// Hook for fetching emails
interface UseEmailsOptions {
  folder?: string
  limit?: number
}

export function useEmails(accountId: string | null, options: UseEmailsOptions = {}) {
  const { folder = 'INBOX', limit = 50 } = options
  const [emails, setEmails] = useState<MailMessage[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [hasMore, setHasMore] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchEmails = useCallback(async (offset = 0, append = false) => {
    if (!accountId) {
      setEmails([])
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      // First try to get from cache
      const client = getNexusClient()
      const { data: cachedEmails, error: cacheError } = await client
        .from('cached_emails')
        .select('*')
        .eq('email_account_id', accountId)
        .eq('folder', folder)
        .eq('is_deleted', false)
        .order('date', { ascending: false })
        .range(offset, offset + limit - 1)
        .returns<CachedEmailRow[]>()

      if (cachedEmails && cachedEmails.length > 0) {
        // Map cached emails to MailMessage format
        const messages: MailMessage[] = cachedEmails.map((e: CachedEmailRow) => ({
          id: e.id,
          uid: e.message_uid,
          folder: e.folder,
          from: {
            address: e.from_address,
            name: e.from_name || undefined,
          },
          to: e.to_addresses || [],
          cc: e.cc_addresses || [],
          subject: e.subject || '(No Subject)',
          snippet: e.snippet || '',
          bodyText: e.body_text || undefined,
          bodyHtml: e.body_html || undefined,
          date: e.date || e.created_at,
          isRead: e.is_read,
          isStarred: e.is_starred,
          hasAttachments: e.has_attachments,
          attachments: e.attachments || [],
          labels: e.labels || [],
        }))

        if (append) {
          setEmails(prev => [...prev, ...messages])
        } else {
          setEmails(messages)
        }

        // Get total count
        const { count } = await client
          .from('cached_emails')
          .select('*', { count: 'exact', head: true })
          .eq('email_account_id', accountId)
          .eq('folder', folder)
          .eq('is_deleted', false)

        setTotalCount(count || 0)
        setHasMore((offset + limit) < (count || 0))
      } else {
        // No cache, try edge function (will populate cache)
        try {
          const result = await mailApi.fetchEmails({
            accountId,
            folder,
            limit,
            offset,
          })

          if (append) {
            setEmails(prev => [...prev, ...result.messages])
          } else {
            setEmails(result.messages)
          }
          setTotalCount(result.totalCount)
          setHasMore(result.hasMore)
        } catch (fetchError: any) {
          // Edge function not available yet, show empty state
          console.warn('Mail fetch not available:', fetchError)
          setEmails([])
          setTotalCount(0)
          setHasMore(false)
        }
      }
    } catch (err: any) {
      console.error('Error fetching emails:', err)
      setError(err.message)
    } finally {
      setIsLoading(false)
    }
  }, [accountId, folder, limit])

  useEffect(() => {
    fetchEmails()
  }, [fetchEmails])

  const loadMore = useCallback(() => {
    if (hasMore && !isLoading) {
      fetchEmails(emails.length, true)
    }
  }, [fetchEmails, emails.length, hasMore, isLoading])

  const refresh = useCallback(() => {
    fetchEmails(0, false)
  }, [fetchEmails])

  return {
    emails,
    totalCount,
    hasMore,
    isLoading,
    error,
    loadMore,
    refresh,
  }
}

// Hook for reading a single email
export function useEmailContent(accountId: string | null, messageUid: string | null, folder: string) {
  const [email, setEmail] = useState<MailMessage | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!accountId || !messageUid) {
      setEmail(null)
      return
    }

    async function fetchEmail() {
      setIsLoading(true)
      setError(null)

      try {
        // First check cache for full content
        const client = getNexusClient()
        const { data: rawCached } = await client
          .from('cached_emails')
          .select('*')
          .eq('email_account_id', accountId as string)
          .eq('message_uid', messageUid as string)
          .eq('folder', folder)
          .single()

        const cached = rawCached as CachedEmailRow | null

        if (cached && (cached.body_text || cached.body_html)) {
          setEmail({
            id: cached.id,
            uid: cached.message_uid,
            folder: cached.folder,
            from: {
              address: cached.from_address,
              name: cached.from_name || undefined,
            },
            to: cached.to_addresses || [],
            cc: cached.cc_addresses || [],
            subject: cached.subject || '(No Subject)',
            snippet: cached.snippet || '',
            bodyText: cached.body_text || undefined,
            bodyHtml: cached.body_html || undefined,
            date: cached.date || cached.created_at,
            isRead: cached.is_read,
            isStarred: cached.is_starred,
            hasAttachments: cached.has_attachments,
            attachments: cached.attachments || [],
            labels: cached.labels || [],
          })
        } else {
          // Fetch from edge function
          try {
            const message = await mailApi.readEmail({
              accountId: accountId!,
              messageUid: messageUid!,
              folder,
            })
            setEmail(message)
          } catch (fetchError: any) {
            console.warn('Mail read not available:', fetchError)
            // Use cached data without body if available
            if (cached) {
              setEmail({
                id: cached.id,
                uid: cached.message_uid,
                folder: cached.folder,
                from: {
                  address: cached.from_address,
                  name: cached.from_name || undefined,
                },
                to: cached.to_addresses || [],
                cc: cached.cc_addresses || [],
                subject: cached.subject || '(No Subject)',
                snippet: cached.snippet || '',
                bodyText: cached.snippet || 'Email content not available. Edge functions need to be deployed.',
                date: cached.date || cached.created_at,
                isRead: cached.is_read,
                isStarred: cached.is_starred,
                hasAttachments: cached.has_attachments,
                attachments: cached.attachments || [],
                labels: cached.labels || [],
              })
            }
          }
        }

        // Mark as read in cache
        if (cached && !cached.is_read) {
          await (client as any)
            .from('cached_emails')
            .update({ is_read: true, updated_at: new Date().toISOString() })
            .eq('id', cached.id)
        }
      } catch (err: any) {
        console.error('Error reading email:', err)
        setError(err.message)
      } finally {
        setIsLoading(false)
      }
    }

    fetchEmail()
  }, [accountId, messageUid, folder])

  return { email, isLoading, error }
}

// Hook for email folders
export function useFolders(accountId: string | null) {
  const [folders, setFolders] = useState<MailFolder[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!accountId) {
      // Return default folders structure
      setFolders(DEFAULT_FOLDERS.map((f, i) => ({
        ...f,
        id: `default-${i}`,
        unreadCount: 0,
        totalCount: 0,
      })))
      return
    }

    async function fetchFolders() {
      setIsLoading(true)
      setError(null)

      try {
        // First check database
        const client = getNexusClient()
        const { data: rawFolders } = await client
          .from('email_folders')
          .select('*')
          .eq('email_account_id', accountId as string)
          .order('name')

        const dbFolders = rawFolders as EmailFolderRow[] | null

        if (dbFolders && dbFolders.length > 0) {
          setFolders(dbFolders.map((f: EmailFolderRow) => ({
            id: f.id,
            name: f.name,
            path: f.path,
            type: f.type as MailFolder['type'],
            unreadCount: f.unread_count,
            totalCount: f.total_count,
          })))
        } else {
          // Try edge function
          try {
            const remoteFolders = await mailApi.getFolders(accountId as string)
            setFolders(remoteFolders)
          } catch (fetchError) {
            // Use defaults
            setFolders(DEFAULT_FOLDERS.map((f, i) => ({
              ...f,
              id: `default-${i}`,
              unreadCount: 0,
              totalCount: 0,
            })))
          }
        }
      } catch (err: any) {
        console.error('Error fetching folders:', err)
        setError(err.message)
        // Fall back to defaults
        setFolders(DEFAULT_FOLDERS.map((f, i) => ({
          ...f,
          id: `default-${i}`,
          unreadCount: 0,
          totalCount: 0,
        })))
      } finally {
        setIsLoading(false)
      }
    }

    fetchFolders()
  }, [accountId])

  return { folders, isLoading, error }
}

// Hook for sending emails
export function useSendEmail() {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const sendEmail = useCallback(async (params: {
    accountId: string
    to: string[]
    cc?: string[]
    bcc?: string[]
    subject: string
    body: string
    isHtml?: boolean
  }): Promise<boolean> => {
    setIsLoading(true)
    setError(null)

    try {
      await mailApi.sendEmail(params)
      return true
    } catch (err: any) {
      console.error('Error sending email:', err)
      setError(err.message)
      return false
    } finally {
      setIsLoading(false)
    }
  }, [])

  return { sendEmail, isLoading, error }
}

// Hook for email actions (mark read, star, delete, etc.)
export function useEmailActions() {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const performAction = useCallback(async (
    accountId: string,
    messageUids: string[],
    action: 'markRead' | 'markUnread' | 'star' | 'unstar' | 'delete' | 'move' | 'archive',
    targetFolder?: string
  ): Promise<boolean> => {
    setIsLoading(true)
    setError(null)

    try {
      // Update local cache first
      const client = getNexusClient()

      const updates: Record<string, any> = { updated_at: new Date().toISOString() }

      switch (action) {
        case 'markRead':
          updates.is_read = true
          break
        case 'markUnread':
          updates.is_read = false
          break
        case 'star':
          updates.is_starred = true
          break
        case 'unstar':
          updates.is_starred = false
          break
        case 'delete':
          updates.is_deleted = true
          updates.folder = 'Trash'
          break
        case 'archive':
          updates.folder = 'Archive'
          break
        case 'move':
          if (targetFolder) {
            updates.folder = targetFolder
          }
          break
      }

      await (client as any)
        .from('cached_emails')
        .update(updates)
        .eq('email_account_id', accountId)
        .in('message_uid', messageUids)

      // Try to sync with server via edge function
      try {
        await mailApi.emailAction({
          accountId,
          messageUids,
          action,
          targetFolder,
        })
      } catch (syncError) {
        console.warn('Failed to sync action to server:', syncError)
        // Local update succeeded, server sync can happen later
      }

      return true
    } catch (err: any) {
      console.error('Error performing email action:', err)
      setError(err.message)
      return false
    } finally {
      setIsLoading(false)
    }
  }, [])

  return { performAction, isLoading, error }
}

// Main hook that combines account selection with email operations
export function useWebmail() {
  const { adminUser } = useAuth()
  const { accounts, isLoading: accountsLoading } = useEmailAccounts({
    userId: adminUser?.id,
    activeOnly: true,
  })

  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null)
  const [selectedFolder, setSelectedFolder] = useState('INBOX')
  const [selectedEmailUid, setSelectedEmailUid] = useState<string | null>(null)

  // Auto-select first account when accounts load
  useEffect(() => {
    if (accounts.length > 0 && !selectedAccountId) {
      setSelectedAccountId(accounts[0].id)
    }
  }, [accounts, selectedAccountId])

  const selectedAccount = accounts.find(a => a.id === selectedAccountId) || null

  return {
    // Account state
    accounts,
    selectedAccount,
    selectedAccountId,
    setSelectedAccountId,
    accountsLoading,

    // Folder state
    selectedFolder,
    setSelectedFolder,

    // Email selection state
    selectedEmailUid,
    setSelectedEmailUid,
  }
}
